import React, { memo, useCallback, useMemo, useState } from 'react';
import { TransitionItem } from '../../items/transition/transition-item-type';
import { isLeftClick } from '../../utils/is-left-click';
import { useCurrentStateAsRef, useWriteContext } from '../../utils/use-context';
import { useTimelineContainerAutoScroll } from '../../utils/use-timeline-container-auto-scroll';
import { onTransitionExtendHandler } from './on-transition-extend-handler';

interface TimelineTransitionExtendHandlesProps {
  transition: TransitionItem;
  timelineWidth: number;
  width: number;
  height: number;
}

const EXTEND_HANDLE_TARGET_WIDTH = 6;

// 리사이즈 핸들 컴포넌트
const TransitionExtendHandle: React.FC<
  React.ComponentProps<'div'> & {
    position: 'left' | 'right';
    handleWidth: number;
  }
> = ({ className, position, handleWidth, style, ...restProps }) => {
  return (
    <div
      className={`group absolute top-0 bottom-0 flex items-center justify-center ${className ?? ''}`}
      style={{
        [position]: -1,
        width: handleWidth,
        cursor: position === 'left' ? 'e-resize' : 'w-resize',
        zIndex: 10,
        ...style,
      }}
      {...restProps}
    />
  );
};

export const TimelineTransitionExtendHandles = memo(
  ({
    transition,
    timelineWidth,
    width,
    height,
  }: TimelineTransitionExtendHandlesProps) => {
    const { setState } = useWriteContext();
    const stateAsRef = useCurrentStateAsRef();
    const [isDragging, setIsDragging] = useState(false);

    // 드래그 중 자동 스크롤
    useTimelineContainerAutoScroll({
      isDragging,
      edgeThreshold: 10,
      maxScrollSpeed: 5,
      xAxis: true,
      yAxis: false,
    });

    const onPointerDownHandleLeft = useCallback(
      (pointerDownEvent: React.PointerEvent<HTMLDivElement>) => {
        if (!isLeftClick(pointerDownEvent)) {
          return;
        }

        setIsDragging(true);
        pointerDownEvent.preventDefault();
        pointerDownEvent.stopPropagation();

        onTransitionExtendHandler({
          pointerDownEvent,
          setState,
          timelineWidth,
          stateAsRef,
          transitionId: transition.id,
          side: 'left',
          height,
          onDragEnd: () => setIsDragging(false),
        });
      },
      [setState, stateAsRef, timelineWidth, transition.id, height],
    );

    const onPointerDownHandleRight = useCallback(
      (pointerDownEvent: React.PointerEvent<HTMLDivElement>) => {
        if (!isLeftClick(pointerDownEvent)) {
          return;
        }

        setIsDragging(true);
        pointerDownEvent.preventDefault();
        pointerDownEvent.stopPropagation();

        onTransitionExtendHandler({
          pointerDownEvent,
          setState,
          timelineWidth,
          stateAsRef,
          transitionId: transition.id,
          side: 'right',
          height,
          onDragEnd: () => setIsDragging(false),
        });
      },
      [setState, stateAsRef, timelineWidth, transition.id, height],
    );

    // 핸들 너비 계산 (아이템이 작으면 더 작게)
    const handleWidth = useMemo(() => {
      return Math.max(1, Math.min(width / 2, EXTEND_HANDLE_TARGET_WIDTH));
    }, [width]);

    return (
      <>
        {transition.transitionType !== 'capsuleCover' && (
          <TransitionExtendHandle
            position="left"
            handleWidth={handleWidth}
            onPointerDown={onPointerDownHandleLeft}
          />
        )}
        <TransitionExtendHandle
          position="right"
          handleWidth={handleWidth}
          onPointerDown={onPointerDownHandleRight}
        />
      </>
    );
  },
);

TimelineTransitionExtendHandles.displayName = 'TimelineTransitionExtendHandles';
